import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { PhSun, PhMoon, PhCloudSun, PhGraduationCap } from 'ember-phosphor-icons';
import BetaHomeAlertRow from 'tio-employee/components/beta/home/alert-row';
import BetaHomeVerifyPhoneModal from 'tio-employee/components/beta/home/verify-phone-modal';
import BetaHomeDiscoverCard from 'tio-employee/components/beta/home/discover-card';
import BetaHomeProgressCard from 'tio-employee/components/beta/home/progress-card';
import BetaHomeStatCard from 'tio-employee/components/beta/home/stat-card';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import { Section, VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import { buildBetaHomeModel } from 'tio-employee/utils/beta/home-model';
import { betaHomeFlags } from 'tio-employee/utils/beta/home-flags';
import type { BetaAlert, BetaHomeFlags, BetaHomeModel } from 'tio-employee/utils/beta/home-model';
import type FeaturesService from 'tio-common/services/features';
import type { IntlService } from 'ember-intl';
import type BetaBrandService from 'tio-employee/services/beta-brand';
import type PartnerService from 'tio-employee/services/partner';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import type { BetaHomeRouteModel } from 'tio-employee/routes/authenticated/dashboard';

// Past four the panel reads as a backlog rather than as triage; the header
// keeps the full count so collapsing never hides how much is waiting.
const ATTENTION_ROWS_SHOWN = 4;

export interface BetaHomeSignature {
  Args: {
    model: BetaHomeRouteModel;
  };
  Element: HTMLDivElement;
}

/**
 * Beta home page — Brand Updates 2026.
 *
 * Design source: Claude Design project 9190654a-6db9-428c-b7ef-5cbb8d5ee149,
 * file `Home Page Concepts.dc.html`.
 *
 * Renders one of the design's two user states. All field mapping lives in
 * `utils/beta/home-model.ts`; this component only supplies services-derived
 * inputs and lays the sections out.
 *
 * Composes from the shared layout system the rest of the app standardizes on
 * (DESIGN.md § Page composition): a `VStack` at the landing-page rhythm, the
 * hero as a `PageHero`, and every other zone a `Section` panel. The scoped
 * `--beta-*` palette stays on the cards inside — it is the design's palette,
 * not the shipped one, and the full-page preview at /reimagined/home-2026
 * shares those components on a dark wrapper where the global tokens would not
 * follow.
 *
 * NOT YET IN SCOPE (see BETA_HOME_GAPS.md): the design also restyles the
 * sidebar and the top-level page chrome. Those are app-shell changes affecting
 * every route, so they are a separate change — this component renders inside
 * the existing chrome.
 */
export default class BetaHome extends Component<BetaHomeSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare partner: PartnerService;
  @service declare reimaginedData: ReimaginedDataService;
  @service('beta-brand') declare betaBrand: BetaBrandService;
  @service declare features: FeaturesService;
  @service declare intl: IntlService;

  @tracked isVerifyingPhone = false;
  @tracked isShowingAllAlerts = false;

  get firstName(): string {
    return (
      this.sessionContext.user?.person?.firstName ||
      this.sessionContext.currentEmployee?.firstName ||
      ''
    );
  }

  get flags(): BetaHomeFlags {
    return betaHomeFlags(this.partner, this.reimaginedData, this.features);
  }

  /**
   * Reading the employee's state here is what makes the row disappear the moment a
   * number is verified, with no reload.
   */
  get phoneCapture() {
    return {
      state: this.sessionContext.currentEmployee?.phoneVerificationState ?? 'none',
      title: this.intl.t('phone_verification.dashboard_card.title'),
      description: this.intl.t('phone_verification.dashboard_card.description'),
      cta: this.intl.t('phone_verification.dashboard_card.cta'),
    };
  }

  openAlertAction = () => {
    this.isVerifyingPhone = true;
  };

  closeVerifyPhone = () => {
    this.isVerifyingPhone = false;
  };

  get home(): BetaHomeModel {
    return buildBetaHomeModel({
      model: this.args.model.reimagined,
      firstName: this.firstName,
      hour: new Date().getHours(),
      actionItems: this.args.model.actionItems,
      flags: this.flags,
      phoneCapture: this.phoneCapture,
      tuitionYearToDate: this.args.model.tuitionYearToDate,
    });
  }

  get greetingIcon() {
    const hour = new Date().getHours();
    if (hour < 12) return PhSun;
    if (hour < 17) return PhCloudSun;
    return PhMoon;
  }

  /** The design's section heading changes with the user state. */
  get discoverHeading(): string {
    return this.home.audience === 'new' ? 'Explore Your Benefits' : 'More to Explore';
  }

  get visibleAlerts(): BetaAlert[] {
    const { alerts } = this.home;
    return this.isShowingAllAlerts ? alerts : alerts.slice(0, ATTENTION_ROWS_SHOWN);
  }

  get collapsedAlertCount(): number {
    return Math.max(0, this.home.alerts.length - ATTENTION_ROWS_SHOWN);
  }

  toggleAllAlerts = () => {
    this.isShowingAllAlerts = !this.isShowingAllAlerts;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="tio-beta-home"
      data-beta-brand={{this.betaBrand.brand}}
      data-test-beta-home={{this.home.audience}}
      ...attributes
    >
      <div class="tio-beta-home__main">
        <VStack class="gap-8">
          {{! ── Hero ──────────────────────────────────────────────────── }}
          <PageHero
            @gradient="brand-soft"
            class="tio-elevation-card-raised"
            data-test-beta-hero={{this.home.audience}}
          >
            {{#if (eq this.home.audience "new")}}
              <div class="tio-beta-home__hero-body tio-beta-home__hero-body--new">
                <div class="tio-beta-home__hero-copy">
                  <div class="tio-col-tight">
                    <div class="tio-beta-home__greeting">
                      {{#let this.greetingIcon as |GreetingIcon|}}
                        <GreetingIcon @size={{20}} />
                      {{/let}}
                      <span>{{this.home.greeting}}, {{this.home.firstName}}</span>
                    </div>
                    <Heading @level={{1}}>Welcome to Your Education Benefits</Heading>
                  </div>

                  {{! The design's lede is TA-specific. A partner without TA would
                      otherwise be welcomed to a benefit they do not offer. }}
                  {{#if this.flags.showTuitionAssistance}}
                    <p class="tio-beta-home__lede">
                      Whether you’re mastering a new skill or earning a new credential, Tuition
                      Assistance is here to help cover the costs.
                    </p>
                  {{else}}
                    <p class="tio-beta-home__lede">
                      Your employer has education benefits waiting for you. Take a look at what’s
                      available.
                    </p>
                  {{/if}}

                  {{#if this.partner.publicTheme}}
                    <Button @intent="primary" @href="#beta-home-explore">Learn More</Button>
                  {{else}}
                    <a
                      href="#beta-home-explore"
                      class="tio-text-sm-semibold whitespace-nowrap app-text-link app-text-link--icon"
                    >Learn More</a>
                  {{/if}}
                </div>

                {{#if this.flags.showTuitionAssistance}}
                  <Card @radius="nested" @padding="lg" class="tio-beta-home__hero-aside">
                    <div class="tio-col">
                      <div class="tio-beta-home__hero-aside-label">Available Tuition Assistance</div>
                      <div
                        class="tio-beta-home__hero-aside-value"
                      >{{this.home.availableTuition}}</div>
                    </div>
                    <div class="tio-beta-home__meta">
                      <span>grow your skills for less</span>
                      <PhGraduationCap @size={{16}} />
                    </div>
                  </Card>
                {{/if}}
              </div>
            {{else}}
              <div class="tio-beta-home__hero-body tio-beta-home__hero-body--active">
                <div class="tio-beta-home__hero-head">
                  <div class="tio-col-tight">
                    <div class="tio-beta-home__greeting">
                      {{#let this.greetingIcon as |GreetingIcon|}}
                        <GreetingIcon @size={{20}} />
                      {{/let}}
                      <span>{{this.home.greeting}}, {{this.home.firstName}}</span>
                    </div>
                    <Heading @level={{1}}>Your Education Benefits Dashboard</Heading>
                  </div>

                  {{#if this.partner.publicTheme}}
                    <Button @intent="primary" @href="#beta-home-explore">View Benefits</Button>
                  {{else}}
                    <a
                      href="#beta-home-explore"
                      class="tio-text-sm-semibold whitespace-nowrap app-text-link app-text-link--icon"
                    >View Benefits</a>
                  {{/if}}
                </div>

                <div class="tio-beta-home__grid tio-beta-home__grid--quad">
                  {{#each this.home.stats as |stat|}}
                    <BetaHomeStatCard @stat={{stat}} />
                  {{/each}}
                </div>
              </div>
            {{/if}}
          </PageHero>

          {{! ── Needs Your Attention ──────────────────────────────────── }}
          {{#if this.home.alerts.length}}
            <Section data-test-beta-home-attention-panel>
              <:header>
                Needs Your Attention
                <span class="tio-beta-home__attention-count">({{this.home.alerts.length}})</span>
              </:header>
              <:body>
                <div class="grid grid-cols-1 gap-4">
                  {{#each this.visibleAlerts as |alert|}}
                    <BetaHomeAlertRow @alert={{alert}} @onAction={{this.openAlertAction}} />
                  {{/each}}

                  {{#if this.collapsedAlertCount}}
                    <div class="flex justify-center">
                      <Button
                        @appearance="minimal"
                        @size="sm"
                        @testHook="beta-home__attention-toggle"
                        {{on "click" this.toggleAllAlerts}}
                        aria-expanded={{if this.isShowingAllAlerts "true" "false"}}
                        data-test-beta-home-attention-toggle
                      >
                        {{#if this.isShowingAllAlerts}}
                          Show less
                        {{else}}
                          Show all ({{this.home.alerts.length}})
                        {{/if}}
                      </Button>
                    </div>
                  {{/if}}
                </div>
              </:body>
            </Section>
          {{/if}}

          {{! ── Active Benefits ───────────────────────────────────────── }}
          {{#if this.home.progressCards.length}}
            <Section data-test-beta-home-active-panel>
              <:header>Active Benefits</:header>
              <:body>
                <div class="tio-beta-home__grid">
                  {{#each this.home.progressCards as |card|}}
                    <BetaHomeProgressCard @card={{card}} />
                  {{/each}}
                </div>
              </:body>
            </Section>
          {{/if}}

          {{! ── Explore Your Benefits / More to Explore ───────────────── }}
          {{#if this.home.discover.length}}
            <Section id="beta-home-explore" data-test-beta-home-discover-panel>
              <:header>{{this.discoverHeading}}</:header>
              <:body>
                <div class="tio-beta-home__grid tio-beta-home__grid--quad">
                  {{#each this.home.discover as |card|}}
                    <BetaHomeDiscoverCard @card={{card}} />
                  {{/each}}
                </div>
              </:body>
            </Section>
          {{/if}}

          {{! ── Resources ─────────────────────────────────────────────── }}
          <Section data-test-beta-home-resources-panel>
            <:header>Resources</:header>
            <:body>
              <div class="tio-beta-home__grid tio-beta-home__grid--quad">
                {{#each this.home.resources as |card|}}
                  <BetaHomeDiscoverCard @card={{card}} />
                {{/each}}
              </div>
            </:body>
          </Section>
        </VStack>
      </div>
    </div>

    <BetaHomeVerifyPhoneModal
      @isOpen={{this.isVerifyingPhone}}
      @onClose={{this.closeVerifyPhone}}
      @employee={{this.sessionContext.currentEmployee}}
    />
  </template>
}
